import { useCallback, useEffect } from 'react';
import { t } from 'i18next';

import { useVoiceCallDirection } from './useVoiceCallDirection';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { callIdSelector } from 'features/call/call-base/store/selectors';
import { deafPhoneNumberByModeSelector } from 'features/call/call-deaf/store';
import { deafPhoneNumberIsHiddenSelector } from 'features/deaf-phone-number-hidden/store';
import { handleError } from 'features/notification/store';
import {
  createHearingParticipant,
  createMonitoringParticipant,
} from 'features/voice-meeting/helpers';
import {
  useVoiceMeetingAudio,
  useVoiceMeetingContext,
} from 'features/voice-meeting/hooks';
import type {
  Participant,
  SessionHearingParticipant,
  VoiceSessionAddParticipantRequest,
} from 'features/voice-session/interfaces';
import { VoiceSessionDaoService } from 'features/voice-session/services';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { selectIsInCallerSession } from 'features/caller-session/store';
import { LogLevel } from 'features/analytics/enums';
import { setCallDetailsCalleeStatus } from 'features/call/call-details/store';
import { CallDetailsStatus } from 'features/call/call-details/enums';
import { deleteVoiceSessionThunk } from 'features/voice-session/store/voiceSessionThunks';

export const useVoiceMeetingSession = () => {
  const dispatch = useAppDispatch();
  const { service } = useVoiceMeetingContext();
  const { getCurrentMeetingAudioStream } = useVoiceMeetingAudio();
  const callId = useAppSelector(callIdSelector);
  const deafPhoneNumberIsHidden = useAppSelector(
    deafPhoneNumberIsHiddenSelector
  );
  const deafPhoneNumberByMode = useAppSelector(deafPhoneNumberByModeSelector);
  const isInCallerSession = useAppSelector(selectIsInCallerSession);

  useVoiceCallDirection();

  // Cleans up any  chime session if for any reason it survives outside a caller session.
  useEffect(() => {
    // TODO: Consider writing a test for this effect
    if (service.meetingSession && !isInCallerSession) {
      service.completeMeetingSession();
      dispatch(
        sendAnalyticsInfo({
          Method: 'useVoiceMeetingSession',
          Level: LogLevel.INFO,
          Message:
            'Session is not in caller session and meetingSession not null, completing meeting session',
        })
      );
    }
  }, [dispatch, isInCallerSession, service]);

  const completeMeeting = async () => {
    if (!service.meetingSession) {
      console.warn('DEBUG: Meeting session not found');
      return;
    }
    service.completeMeetingSession();
  };

  const startMeeting = async () => {
    if (!service.meetingSession) {
      console.warn('DEBUG: Meeting session not found');
      return;
    }

    console.log('DEBUG: Step 2: Starting meeting session');
    service.meetingSession.audioVideo.start();
    await getCurrentMeetingAudioStream();
  };

  const addParticipant = async (phoneNumber: string) => {
    const payload = getVoiceSessionPayload(phoneNumber);
    const newSessionState =
      await VoiceSessionDaoService.addParticipant(payload);
    service.setVoiceSession(newSessionState);

    return service.getHearingByPhoneNumber(phoneNumber);
  };

  const addMonitorParticipant = async (
    adminContact: string,
    voiceSessionId: string
  ) => {
    const participant = createMonitoringParticipant(adminContact);
    await VoiceSessionDaoService.addParticipant({
      participant,
      sessionId: voiceSessionId,
      callId,
    });
  };

  const disconnectParticipant = async ({
    participantId,
    sessionId,
    sessionParticipants,
  }: {
    participantId: string;
    sessionId: string;
    sessionParticipants: Participant[];
  }) => {
    try {
      const participantExists = sessionParticipants.some(
        (p) => p.id === participantId
      );

      if (!participantExists) {
        return;
      }

      await VoiceSessionDaoService.deleteParticipant({
        sessionId,
        participantId,
      });
    } catch (error) {
      dispatch(
        handleError({
          error,
          title: t('hearingDisconnectFailed'),
          methodName: 'disconnectParticipant',
        })
      );
    }
  };

  const disconnectParticipants = async (participantIds: string[]) => {
    const sessionId = service.getSessionId();

    try {
      const voiceSession =
        await VoiceSessionDaoService.getVoiceSessionById(sessionId);

      await Promise.allSettled(
        participantIds.map((participantId) =>
          disconnectParticipant({
            participantId,
            sessionId,
            sessionParticipants: voiceSession.participants,
          })
        )
      );
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'disconnectParticipants',
        })
      );
    } finally {
      dispatch(
        // INFO: Disconnecting non-primary hearing will be addressed in https://sorenson.atlassian.net/browse/ARC-2765
        setCallDetailsCalleeStatus({
          isDeafParticipant: false,
          status: CallDetailsStatus.CALLEE_RECONNECT,
        })
      );
      //removing finally block as CallDetailStatus will be updated in useCallDetailStatusHandler
    }
  };

  const getSessionAndReturnHearingParticipants = useCallback(async (): Promise<
    SessionHearingParticipant[]
  > => {
    const sessionId = service.getSessionId();

    if (!sessionId) {
      return [];
    }

    try {
      const voiceSession =
        await VoiceSessionDaoService.getVoiceSessionById(sessionId);

      return voiceSession.participants.filter(
        ({ type }) => type === 'hearing'
      ) as SessionHearingParticipant[];
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'getSessionAndReturnParticipants',
        })
      );
      return [];
    }
  }, [dispatch, service]);

  const getSessionAndCheckIfDisconnected = useCallback(
    async (participantId: string) => {
      const sessionId = service.getSessionId();

      if (!sessionId) {
        return true;
      }

      try {
        const voiceSession =
          await VoiceSessionDaoService.getVoiceSessionById(sessionId);
        const participant = voiceSession.participants.find(
          (p: Participant) => p.id === participantId
        );
        return participant?.state === 'disconnected';
      } catch (error) {
        dispatch(
          handleError({
            error,
            methodName: 'getSessionAndCheckIfDisconnected',
            disableUserNotification: true,
          })
        );
        return true;
      }
    },
    [dispatch, service]
  );

  const updateVoiceSessionFromSwitchboard = useCallback(async () => {
    const sessionId = service.getSessionId();

    if (!sessionId) {
      return;
    }

    const updatedVoiceSession =
      await VoiceSessionDaoService.getVoiceSessionById(sessionId);

    service.setVoiceSession(updatedVoiceSession);
  }, [service]);

  const deleteVoiceSession = useCallback(async () => {
    const sessionId = service.getSessionId();

    if (!sessionId) {
      return;
    }
    dispatch(deleteVoiceSessionThunk());
  }, [dispatch, service]);

  const getVoiceSessionPayload = (
    phoneNumber: string
  ): VoiceSessionAddParticipantRequest => {
    const participant = createHearingParticipant(
      phoneNumber,
      deafPhoneNumberByMode
    );
    const sessionId = service.getSessionId();

    return {
      participant,
      sessionId,
      ...(deafPhoneNumberIsHidden ? { callId } : {}),
    };
  };

  return {
    completeMeeting,
    deleteVoiceSession,
    startMeeting,
    addParticipant,
    addMonitorParticipant,
    disconnectParticipants,
    getSessionAndCheckIfDisconnected,
    getSessionAndReturnHearingParticipants,
    updateVoiceSessionFromSwitchboard,
  };
};
